import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, forkJoin, BehaviorSubject } from 'rxjs';
import * as rxOp from 'rxjs/operators';
import { catchError, map, tap, finalize, shareReplay } from 'rxjs/operators';
import { Activity, PaginatedList } from '../models';
import { UtilsService } from './utils.service';
import _ from 'lodash';
import { Post } from '../models/post';
import { Invite } from '../models/invite';
import moment from 'moment';
import { PostService } from './post.service';

export interface PreloadedInvitesData {
  allInvites: Invite[];
  invitesToMe: Invite[];
  invitesFromMe: Invite[];
  myUpcomingPosts: Post[];
  loadedAt: number;
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({ providedIn: 'root' })
export class InviteService {
  private preloadedData: PreloadedInvitesData | null = null;
  public preloadedData$ = new BehaviorSubject<PreloadedInvitesData | null>(null);
  public isPreloading = false;
  public hasRedirectedToPending = false;
  public hasRedirectedToSplit = false;
  public hasRedirectedToPay = false;

  constructor(
    private http: HttpClient,
    private utils: UtilsService,
    private postService: PostService
  ) {
    const today = moment();
    this.current_week = {
      start: today.clone().startOf('week'),
      end: today.clone().endOf('week'),
    };
    this.last_week = {
      start: this.current_week.start.clone().subtract(7, 'days'),
      end: this.current_week.end.clone().subtract(7, 'days'),
    };
    this.next_week = {
      start: this.current_week.start.clone().add(7, 'days'),
      end: this.current_week.end.clone().add(7, 'days'),
    };
  }

  getPreloadedData(): PreloadedInvitesData | null {
    return this.preloadedData;
  }

  setPreloadedData(data: PreloadedInvitesData): void {
    this.preloadedData = data;
    this.preloadedData$.next(data);
    if (this.utils?.appState) {
      if (data.allInvites) this.utils.appState.invites.next(data.allInvites);
      if (data.invitesToMe) this.utils.appState.invitesToMe.next(data.invitesToMe);
      if (data.invitesFromMe) this.utils.appState.invitesFromMe.next(data.invitesFromMe);
      if (data.myUpcomingPosts && this.utils.appState.myUpcomingPosts) {
        this.utils.appState.myUpcomingPosts.next(data.myUpcomingPosts);
      }
    }
  }

  private inFlightPreload$: Observable<PreloadedInvitesData | null> | null = null;
  private inFlightUnreviewed$: Observable<Invite[]> | null = null;
  private unreviewedCache: { timestamp: number; data: Invite[] } | null = null;

  preloadUpcomingInvites(userId: string | number): Observable<PreloadedInvitesData | null> {
    if (!userId) return of(null);

    // 1. Cache hit under 90s
    if (this.preloadedData && (Date.now() - this.preloadedData.loadedAt < 90000)) {
      return of(this.preloadedData);
    }

    // 2. Return in-flight observable to deduplicate simultaneous calls
    if (this.inFlightPreload$) {
      return this.inFlightPreload$;
    }

    this.isPreloading = true;
    const startOfLastWeek = moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD');

    // Single query for all user invites (to OR from) since start of last week
    const filtersAll: any = {
      limit: 100,
      toOrFrom: userId,
      datetime_after: startOfLastWeek,
      expand: 'post,post.creator,post.activity,post.images.photos,post.activity.images.photos,activity,activity.images.photos,ifrom,to,location'
    };

    const posts$ = this.postService.list({
      creator: userId,
      limit: 50,
      expand: 'activity,activity.images,activity.images.photos,images,images.photos,location'
    }).pipe(catchError(() => of([])));

    const invites$ = this.list(filtersAll).pipe(catchError(() => of([])));
    const unreviewed$ = this.fetchUnreviewedInvites(userId).pipe(catchError(() => of([])));

    const req$ = forkJoin({
      posts: posts$,
      invites: invites$,
      unreviewed: unreviewed$
    }).pipe(
      map(({ posts, invites, unreviewed }) => {
        const postsList = (posts as any)?.results || posts || [];
        const myUpcomingPosts = (Array.isArray(postsList) ? postsList : []).filter((p: any) => {
          if (!p.datetime) return true;
          return moment(p.datetime).isSameOrAfter(moment().subtract(6, 'hours'));
        });

        const allFetched: any[] = Array.isArray(invites) ? invites : (invites as any)?.results || [];
        const invitesToMe = allFetched.filter((inv: any) => {
          const toId = (inv.to as any)?.id !== undefined ? (inv.to as any).id : inv.to;
          return toId != null && String(toId) === String(userId);
        });
        const invitesFromMe = allFetched.filter((inv: any) => {
          const fromId = (inv.ifrom as any)?.id !== undefined ? (inv.ifrom as any).id : inv.ifrom;
          return fromId != null && String(fromId) === String(userId);
        });

        const unreviewedInvites = (unreviewed as any) || [];
        const activeCombined = _.uniqBy([...invitesToMe, ...invitesFromMe, ...unreviewedInvites], 'id');
        const allInvites = activeCombined.sort((a, b) => {
          const timeA = a.datetime ? new Date(a.datetime).getTime() : 0;
          const timeB = b.datetime ? new Date(b.datetime).getTime() : 0;
          return timeA - timeB;
        });

        const result: PreloadedInvitesData = {
          allInvites,
          invitesToMe,
          invitesFromMe,
          myUpcomingPosts,
          loadedAt: Date.now()
        };

        this.setPreloadedData(result);
        return result;
      }),
      catchError(() => of(null)),
      finalize(() => {
        this.isPreloading = false;
        this.inFlightPreload$ = null;
      }),
      shareReplay(1)
    );

    this.inFlightPreload$ = req$;
    return req$;
  }

  fetchUnreviewedInvites(userId: string | number): Observable<Invite[]> {
    if (!userId) return of([]);

    // 1. Cache hit under 90s
    if (this.unreviewedCache && (Date.now() - this.unreviewedCache.timestamp < 90000)) {
      return of(this.unreviewedCache.data);
    }

    // 2. Return in-flight observable to deduplicate simultaneous calls
    if (this.inFlightUnreviewed$) {
      return this.inFlightUnreviewed$;
    }

    const nowStr = moment().format('YYYY-MM-DD HH:mm:ss');
    const expand = 'post,post.creator,post.activity,post.images.photos,post.activity.images.photos,activity,activity.images.photos,ifrom,to,location';

    // Single query for past unreviewed invites using toOrFrom and status list filter
    const queryParams: any = {
      toOrFrom: userId,
      datetime_before: nowStr,
      status: 'accepted,accepted-read,paid,paid-read,reviewed-from,reviewed-to,accepted-yes',
      limit: 50,
      expand
    };

    const req$ = this.list(queryParams).pipe(
      catchError(() => of([])),
      map((res) => {
        const flat: Invite[] = Array.isArray(res) ? res : (res as any)?.results || [];
        const unique = _.uniqBy(flat, 'id');
        const filtered = unique.filter((inv) => {
          if (!inv || !inv.datetime) return false;
          if (!moment(inv.datetime).isBefore(moment())) return false;
          const s = (inv.status || '').toLowerCase();
          const fromId = (inv.ifrom as any)?.id !== undefined ? (inv.ifrom as any).id : inv.ifrom;
          const toId = (inv.to as any)?.id !== undefined ? (inv.to as any).id : inv.to;
          const isFromMe = fromId != null && String(fromId) === String(userId);
          const isToMe = toId != null && String(toId) === String(userId);
          if (!isFromMe && !isToMe) return false;
          if (s === 'reviewed-both' || s.includes('reviewed-both')) return false;
          if (s === 'reviewed-from' && isFromMe) return false;
          if (s === 'reviewed-to' && isToMe) return false;
          return true;
        });

        this.unreviewedCache = { timestamp: Date.now(), data: filtered };
        return filtered;
      }),
      catchError(() => of([])),
      finalize(() => {
        this.inFlightUnreviewed$ = null;
      }),
      shareReplay(1)
    );

    this.inFlightUnreviewed$ = req$;
    return req$;
  }

  list(qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:invites-list');
    return this.http
      .get<PaginatedList>(url, this.utils.requestOptions(qp))
      .pipe(
        rxOp.map((resp: PaginatedList) => {
          resp.results = Invite.fromList(resp.results);
          return resp.results;
        })
      );
  }

  listWithPagination(qp?: Object): Observable<PaginatedList> {
    const url = this.utils.getUrl('rest:invites-list');
    return this.http
      .get<PaginatedList>(url, this.utils.requestOptions(qp))
      .pipe(
        rxOp.map((resp: PaginatedList) => {
          if (resp?.results) {
            resp.results = Invite.fromList(resp.results);
          }
          return resp;
        })
      );
  }

  get(id: string, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:post-detail', id);
    return this.http.get(url, this.utils.requestOptions(qp));
  }

  delete(id: string, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:post-detail', id);
    return this.http.delete(url, this.utils.requestOptions(qp));
  }

  create(obj: Object, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:invites-list');
    return this.http.post(url, obj, this.utils.requestOptions(qp));
  }

  sendServerInvite(email: string, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:invitation-send-invite');
    return this.http.post(url, { email }, this.utils.requestOptions(qp));
  }

  notify(id: number, channel: string = 'text', qp?: Object): Observable<any> {
    const baseUrl = this.utils.getUrl('rest:invites-list');
    const url = baseUrl.endsWith('/') ? `${baseUrl}${id}/notify/` : `${baseUrl}/${id}/notify/`;
    return this.http.post(url, { channel }, this.utils.requestOptions(qp));
  }

  respond(id: number, obj?: Object, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:invites-respond', id);
    return this.http.post(url, obj, this.utils.requestOptions(qp));
  }

  review(id: number, obj?: Object, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:invites-review', id);
    return this.http.post(url, obj, this.utils.requestOptions(qp));
  }

  reject(id: number, obj?: Object, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:invites-reject', id);
    return this.http.post(url, obj, this.utils.requestOptions(qp));
  }

  update(id: number, obj: Object, qp?: Object): Observable<any> {
    const url = this.utils.getUrl('rest:post-detail', id);
    return this.http.patch(url, obj, this.utils.requestOptions(qp));
  }

  current_week: { [key: string]: moment.Moment };
  last_week: { [k in 'start' | 'end']: moment.Moment };
  next_week: { [k in 'start' | 'end']: moment.Moment };

  calendar(data: Invite[]) {
    // const mapped_data = this.transform_data();
    const grouped = this.group(data);
    const dataset = {};
    dataset['last_week'] = this.week_view(
      grouped.last_week,
      this.last_week.start.clone()
    );
    dataset['current_week'] = this.week_view(
      grouped.current_week,
      this.current_week.start.clone()
    );
    dataset['next_week'] = this.week_view(
      grouped.next_week,
      this.next_week.start.clone()
    );
    dataset['past'] = this.month_view(grouped.past);
    dataset['future'] = this.month_view(grouped.future);
    return dataset;
  }

  /** Convert to month view */
  month_view(invites: Invite[]) {
    const events = {};
    const grouped_by_month = _.groupBy(invites, (invite: Invite) => {
      return moment(invite.datetime).format('YYYY-MM');
    });
    _.each(_.keys(grouped_by_month), (month) => {
      const monthStartDate = moment(month).startOf('month');
      const invites = grouped_by_month[month];
      const month_events = {};
      _.each(
        _.range(0, monthStartDate.clone().endOf('month').date()),
        (day) => {
          const mday = monthStartDate.clone().add(day, 'day');
          const next_day = mday.clone().add(1, 'day');
          const day_invites = [];
          _.each(invites, (invite: Invite) => {
            const date = moment(invite.datetime);
            if (date.isBetween(mday, next_day) || date.isSame(mday)) {
              day_invites.push(invite);
            }
          });
          month_events[mday.format('YYYY-MM-DD')] = day_invites;
        }
      );
      events[month] = month_events;
    });
    return events;
  }

  /** Convert to week view */
  week_view(week_invites: Invite[], week_start_date) {
    const events = {};
    _.each(_.range(0, 7), (day) => {
      const mday = week_start_date.clone().add(day, 'day');
      const next_day = mday.clone().add(1, 'day');
      const day_invites = [];
      _.each(week_invites, (invite: Invite) => {
        const date = moment(invite.datetime);
        if (date.isBetween(mday, next_day)) {
          day_invites.push(invite);
        }
      });

      events[mday.format('YYYY-MM-DD')] = this.groupByActivity(day_invites);
    });
    return events;
  }

  /** Seggregate invites into 5 categories. past, last_week, current_week, next_week, future */
  group(data: Invite[]) {
    const grouped: { [key: string]: Invite[] } = _.groupBy(data, (item) => {
      const date = moment(item.datetime);
      if (date.isBetween(this.current_week.start, this.current_week.end)) {
        return 'current_week';
      } else if (date.isBetween(this.last_week.start, this.last_week.end)) {
        return 'last_week';
      } else if (date.isBetween(this.next_week.start, this.next_week.end)) {
        return 'next_week';
      } else if (date.isBefore(this.last_week.start)) {
        return `past`;
      } else if (date.isAfter(this.next_week.end)) {
        return `future`;
      } else {
        return 'other';
      }
    });
    return grouped;
  }

  ref: number = 0;
  groupByActivity(data: Invite[]) {
    const grouped: { [key: string]: Invite[] } = _.groupBy(data, (item) => {
      if (item.activity != null) {
        return item.activity.name;
      }
    });

    _.each(grouped, (activity) => {
      this.ref++;
      activity.status = this.inviteStatusOverview(activity);
      activity.ref = this.ref;
    });

    return grouped;
  }

  /** Set overall status for grouped invites */
  inviteStatusOverview(invites) {
    if (invites == null) {
      return;
    }

    const total = invites.length;
    const paid = _.filter(invites,(invite: { status: string }) => invite.status == 'paid' || invite.status == 'paid-read' || invite.status == 'paid-cancel' || invite.status == 'paid-canceled').length;
    const accepted = _.filter(invites,(invite: { status: string }) => invite.status == 'accepted' || invite.status == 'accepted-read' || invite.status == 'accepted-yes').length;
    const declinded = _.filter(invites,(invite: { status: string }) =>invite.status == 'declined' || invite.status == 'declined-read' || invite.status == 'accepted-no').length;
    const maybe = _.filter(invites,(invite: { status: string }) =>invite.status == 'maybe' || invite.status == 'maybe-read' || invite.status == 'accepted-maybe').length;
    const pending = _.filter(invites,(invite: { status: string }) => invite.status == 'pending' || invite.status == 'self-paid').length;

    if (paid > 0) {
      return 'paid';
    } 
    else if (accepted > 0) {
      return 'some-accepted';
    } 
    else if (total > 0 && total == declinded) {
      return 'all-declined';
    } 
    else if (maybe > 0) {
      return 'some-maybe';
    } 
    else if (pending > 0) {
      return 'pending';
    } 
    else {
      return '';
    }
  }

  // ERROR LOGGING
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      // console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /** Log a StockService message with the MessageService */
  private log(message: string) {
    console.log(message);
  }
}
